/**
 * The text strata: Title (a heading, level 1-4), Text (running text
 * with a tone), Label (the 11px uppercase tracked caption every panel
 * heading wears), Figure (a number with its unit, tabular digits),
 * and Mono (an identifier).
 */
import type { CSSProperties, ReactNode } from 'react';
import { Link, type LinkProps } from '@tanstack/react-router';
import { FONT, SPACE, TEXT, TRACKING, WEIGHT, usePalette, type Palette } from '../theme';

export type Tone = 'ink' | 'strong' | 'secondary' | 'muted' | 'brand' | 'complying';

export function toneColor(p: Palette, tone: Tone): string {
  switch (tone) {
    case 'ink':
      return p.ink;
    case 'strong':
      return p.inkStrong;
    case 'secondary':
      return p.inkSecondary;
    case 'muted':
      return p.inkMuted;
    case 'brand':
      return p.brandInk;
    case 'complying':
      return p.complying;
  }
}

const TEXT_BY_NAME = new Map(Object.entries(TEXT));

/** The pixels a named type size spans. */
function textSize(size: keyof typeof TEXT): number {
  const pixels = TEXT_BY_NAME.get(size);
  if (pixels === undefined) throw new Error(`no type size named ${size} in TEXT`);
  return pixels;
}

/** The type size of a title at this level. */
function titleSize(level: 1 | 2 | 3 | 4): number {
  switch (level) {
    case 1:
      return TEXT.display;
    case 2:
      return TEXT.headline;
    case 3:
      return TEXT.title;
    case 4:
      return TEXT.subtitle;
  }
}

export function Title({
  level = 1,
  tone = 'strong',
  size,
  children,
  id,
}: {
  level?: 1 | 2 | 3 | 4;
  tone?: Tone;
  /** A size of the type scale in place of the level's own (a screen's title, Figma 3). */
  size?: keyof typeof TEXT;
  children: ReactNode;
  id?: string;
}) {
  const p = usePalette();
  const Tag = `h${level}` as const;
  return (
    <Tag
      id={id}
      style={{
        margin: 0,
        fontSize: size === undefined ? titleSize(level) : textSize(size),
        fontWeight: level === 1 ? WEIGHT.medium : WEIGHT.strong,
        letterSpacing: TRACKING.title,
        lineHeight: 1.15,
        color: toneColor(p, tone),
        textWrap: 'balance',
      }}
    >
      {children}
    </Tag>
  );
}

export type TextSize = 'micro' | 'caption' | 'control' | 'body';

/** The pixel size a text size names. */
export function textSizeOf(size: TextSize): number {
  switch (size) {
    case 'micro':
      return TEXT.micro;
    case 'caption':
      return TEXT.caption;
    case 'control':
      return TEXT.control;
    case 'body':
      return TEXT.body;
  }
}

export function Text({
  tone = 'ink',
  size = 'body',
  weight,
  breakAll = false,
  ellipsis = false,
  measure,
  children,
  as = 'span',
}: {
  tone?: Tone;
  size?: TextSize;
  weight?: (typeof WEIGHT)['regular' | 'medium' | 'strong'];
  /** Long identifiers wrap anywhere rather than overflowing. */
  breakAll?: boolean;
  /** One line, clipped with an ellipsis. */
  ellipsis?: boolean;
  /** The widest a paragraph runs, in pixels: a line a person can read. */
  measure?: number;
  children: ReactNode;
  as?: 'span' | 'p' | 'div';
}) {
  const p = usePalette();
  const Tag = as;
  const style: CSSProperties = {
    margin: 0,
    fontSize: textSizeOf(size),
    fontWeight: weight,
    color: toneColor(p, tone),
    lineHeight: 1.45,
    wordBreak: breakAll ? 'break-all' : undefined,
    maxWidth: measure,
    ...(ellipsis ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' } : {}),
  };
  return <Tag style={style}>{children}</Tag>;
}

export function Label({ children, id }: { children: ReactNode; id?: string }) {
  const p = usePalette();
  return (
    <span
      id={id}
      style={{
        fontSize: TEXT.micro,
        letterSpacing: TRACKING.label,
        textTransform: 'uppercase',
        color: p.inkSecondary,
        fontWeight: WEIGHT.strong,
      }}
    >
      {children}
    </span>
  );
}

export function Figure({
  value,
  unit,
  label,
  paint,
}: {
  value: ReactNode;
  unit?: ReactNode;
  label?: ReactNode;
  /** A meaning paint for the number itself (absence amber for an unpriced count). */
  paint?: string;
}) {
  const p = usePalette();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.xxs, minWidth: 0 }}>
      {label && <Label>{label}</Label>}
      <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: SPACE.xs }}>
        <span
          style={{
            fontSize: 24,
            fontWeight: WEIGHT.strong,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: TRACKING.title,
            color: paint ?? p.inkStrong,
            lineHeight: 1.1,
          }}
        >
          {value}
        </span>
        {unit && <span style={{ fontSize: TEXT.caption, color: p.inkMuted }}>{unit}</span>}
      </span>
    </div>
  );
}

export function Mono({ children, tone = 'secondary', size = 'caption' }: { children: ReactNode; tone?: Tone; size?: TextSize }) {
  const p = usePalette();
  return (
    <code style={{ fontFamily: FONT.mono, fontSize: textSizeOf(size), color: toneColor(p, tone), wordBreak: 'break-all' }}>
      {children}
    </code>
  );
}

/** A link into another screen: the router's Link, worn as text. The
 * one place an anchor gets its colour, because no screen may paint. */
export function RouteLink({
  to,
  params,
  search,
  tone = 'secondary',
  size = 'caption',
  children,
}: {
  to: LinkProps['to'];
  params?: LinkProps['params'];
  search?: LinkProps['search'];
  tone?: Tone;
  size?: TextSize;
  children: ReactNode;
}) {
  const p = usePalette();
  return (
    <Link
      to={to}
      params={params}
      search={search}
      style={{ color: toneColor(p, tone), fontSize: textSizeOf(size), textDecoration: 'none' }}
      activeProps={{ style: { color: p.brandInk, fontSize: textSizeOf(size), textDecoration: 'none' } }}
    >
      {children}
    </Link>
  );
}

/** An outbound link (mailto, a document, another service), worn as text in the brand; `newTab` opens its own tab. */
export function ExternalLink({
  href,
  children,
  newTab = false,
}: {
  href: string;
  children: ReactNode;
  newTab?: boolean;
}) {
  const p = usePalette();
  return (
    <a
      href={href}
      {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      style={{ color: p.brandInk, textDecoration: 'underline', textUnderlineOffset: 2 }}
    >
      {children}
    </a>
  );
}
